import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HERO_OUTCOMES } from './data';

export const BOARD_WIDTH = Math.max(...HERO_OUTCOMES.map((phrase) => phrase.length));

export function padPhrase(phrase, width = BOARD_WIDTH) {
  return phrase.padEnd(width, ' ');
}

export function flapGlyph(char) {
  return char === ' ' ? '\u00a0' : char;
}

function FlapCell({ value, delay = 0, empty = false }) {
  const [top, setTop] = useState(value);
  const [bottom, setBottom] = useState(value);
  const [flipping, setFlipping] = useState(false);
  const currentRef = useRef(value);

  useEffect(() => {
    if (value === currentRef.current) return undefined;

    const timeouts = [];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const start = window.setTimeout(() => {
      if (reduceMotion) {
        currentRef.current = value;
        setTop(value);
        setBottom(value);
        setFlipping(false);
        return;
      }

      setBottom(value);
      setFlipping(true);

      timeouts.push(
        window.setTimeout(() => {
          setTop(value);
          setFlipping(false);
          currentRef.current = value;
        }, 440),
      );
    }, delay);

    timeouts.push(start);

    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [delay, value]);

  return (
    <span className={`cq-flap${flipping ? ' is-flipping' : ''}${empty ? ' cq-flap--empty' : ''}`} aria-hidden="true">
      <span className="cq-flap-half cq-flap-half--top">
        <span>{flapGlyph(top)}</span>
      </span>
      <span className="cq-flap-half cq-flap-half--bottom">
        <span>{flapGlyph(bottom)}</span>
      </span>
    </span>
  );
}

export function DepartureBoardRow({ phrase, width = BOARD_WIDTH }) {
  const padded = padPhrase(phrase, width);

  return (
    <span className="cq-flap-board-wrap">
      <span className="cq-flap-board">
        {padded.split('').map((char, i) => (
          <FlapCell key={i} value={char} delay={0} />
        ))}
      </span>
    </span>
  );
}

export function DepartureBoard({
  phrases = HERO_OUTCOMES,
  interval = 4200,
  pause = false,
  stagger = 28,
}) {
  const [index, setIndex] = useState(0);
  const width = useMemo(
    () => Math.max(BOARD_WIDTH, ...phrases.map((phrase) => phrase.length)),
    [phrases],
  );
  const padded = padPhrase(phrases[index] ?? '', width);
  const chars = padded.split('');
  const lastGlyph = chars.reduce((last, char, i) => (char !== ' ' ? i : last), -1);

  useEffect(() => {
    if (pause || phrases.length < 2) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % phrases.length);
    }, interval);

    return () => window.clearInterval(id);
  }, [interval, pause, phrases.length]);

  return (
    <span className="cq-flap-board-wrap">
      <span className="cq-flap-board" aria-live="polite" aria-label={phrases[index]}>
        {chars.map((char, i) => (
          <FlapCell key={i} value={char} delay={i * stagger} empty={i > lastGlyph} />
        ))}
      </span>
    </span>
  );
}
